import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { Apartment } from '../types/Apartment';
import { apartments as initialApartments } from '../data/apartments';
import { ApartmentService, generateFloorPlanImages, generateBuilding360Images } from '../firebase/utils';
import { onSnapshot, collection, query } from 'firebase/firestore';
import { db } from '../firebase/config';

// Neue Profilbild-URLs entsprechend der Zuordnung A1=1.jpg, B1=2.jpg, A2=3.jpg, B2=4.jpg etc.
const profileImages = [
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113d850e99612050a40_1.jpg', // A1
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113fe99593508f25f01_2.jpg', // B1
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113be99d7ba07271222_3.jpg', // A2
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113cb56ce5a1ea00a74_4.jpg', // B2
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1127081ec278633b0d1_5.jpg', // A3
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1130842b3ce678a6aff_6.jpg', // B3
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113253078ca0c887b44_7.jpg', // A4
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113105219c626276dbb_8.jpg', // B4
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f114a76e2babd6225bb5_9.jpg', // A5
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f114826db4c61ccc7757_10.jpg', // B5
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1149297eaa439e39c8b_11.jpg', // A6
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f115173cbb20c2b99df6_12.jpg', // B6
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f115c00d6d6ea04f3625_13.jpg', // A7
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f11545c2037da5040d9e_14.jpg', // B7
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f115173cbb20c2b99dfb_15.jpg', // A8
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1156fdff45c98ce10b5_16.jpg', // B8
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1166014e8629720934c_17.jpg', // A9
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f11624ae8991696c39c4_18.jpg', // B9
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f11628e1e50ce76e51c2_19.jpg', // A10
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f117fe99593508f25f55_20.jpg', // B10
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1174c79d5e6134fcca4_21.jpg', // A11
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1137418f1dd3e570f09_22.jpg'  // B11
];

// Funktion um das richtige Profilbild basierend auf der Apartment ID zu erhalten
const getProfileImage = (apartmentId: number): string => {
  // A-Wohnungen (ungerade Indizes): ID 1=Index 0, ID 2=Index 2, ID 3=Index 4, etc.
  // B-Wohnungen (gerade Indizes): ID 12=Index 1, ID 13=Index 3, ID 14=Index 5, etc.
  if (apartmentId <= 11) {
    // Haus A: A1=0, A2=2, A3=4, etc.
    return profileImages[(apartmentId - 1) * 2];
  } else {
    // Haus B: B1=1, B2=3, B3=5, etc.
    return profileImages[((apartmentId - 12) * 2) + 1];
  }
};

interface ApartmentContextType {
  apartments: Apartment[];
  loading: boolean;
  error: string | null;
  updateApartmentAvailability: (id: number, available: boolean) => Promise<void>;
  updateApartment: (apartment: Apartment) => Promise<void>;
  resetApartments: () => void;
  useFirestore: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
  lastUpdateTime: Date | null;
}

const ApartmentContext = createContext<ApartmentContextType | undefined>(undefined);

export const useApartments = () => {
  const context = useContext(ApartmentContext);
  if (!context) {
    throw new Error('useApartments must be used within an ApartmentProvider');
  }
  return context;
};

interface ApartmentProviderProps {
  children: ReactNode;
}

export const ApartmentProvider: React.FC<ApartmentProviderProps> = ({ children }) => {
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useFirestore, setUseFirestore] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  // Initialize data - try Firestore first, fallback to static data
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initializeData = async () => {
      setLoading(true);
      setConnectionStatus('reconnecting');
      
      try {
        // Try to fetch from Firestore first
        const firestoreApartments = await ApartmentService.getAllApartments();
        if (firestoreApartments.length > 0) {
          setApartments(firestoreApartments);
          setUseFirestore(true);
          setupPersistentRealtimeListener();
          setLastUpdateTime(new Date());
          console.log('✅ Connected to Firestore with', firestoreApartments.length, 'apartments');
        } else {
          // Fallback to static data
          setApartments(initialApartments);
          setUseFirestore(false);
          setConnectionStatus('disconnected');
          console.log('📂 Using static data');
        }
        setError(null);
      } catch (err) {
        console.warn('⚠️ Firestore not available, using static data:', err);
        setApartments(initialApartments);
        setUseFirestore(false);
        setConnectionStatus('disconnected');
        setError(null); // Don't show error for fallback
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Setup persistent real-time listener with automatic reconnection
  const setupPersistentRealtimeListener = () => {
    if (!useFirestore) return;

    // Clear existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    // Clear reconnection timeout if it exists
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    const apartmentsCollection = collection(db, 'apartments');
    const q = query(apartmentsCollection);

    try {
      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          const updatedApartments: Apartment[] = [];
          snapshot.forEach((doc) => {
            try {
              const data = doc.data();
              // Transform German Firestore fields to TypeScript interface
              const apartmentId = getNumericId(doc.id);
              const cloudfrontUrl = 'https://d2tbav89ixiad0.cloudfront.net';
              

              
              // Generate floor plan filename based on apartment ID using new naming convention
              const getFloorPlanFilename = (id: number): string => {
                const mapping: { [key: number]: string } = {
                  1: 'Wohnung A1.1',
                  2: 'Wohnung A1.3',
                  3: 'Wohnung A1.2',
                  4: 'Wohnung A2.1',
                  5: 'Wohnung A2.3',
                  6: 'Wohnung A2.2',
                  7: 'Wohnung A3.1',
                  8: 'Wohnung A3.3',
                  9: 'Wohnung A3.2',
                  10: 'Wohnung A4.1',
                  11: 'Wohnung A4.2',
                  12: 'Wohnung B1.1',
                  13: 'Wohnung B1.3',
                  14: 'Wohnung B1.2',
                  15: 'Wohnung B2.1',
                  16: 'Wohnung B2.3',
                  17: 'Wohnung B2.2',
                  18: 'Wohnung B3.1',
                  19: 'Wohnung B3.3',
                  20: 'Wohnung B3.2',
                  21: 'Wohnung B4.1',
                  22: 'Wohnung B4.2'
                };
                return `${mapping[id] || `Wohnung A${id}`}.pdf`;
              };
              
              const apartment: Apartment = {
                id: apartmentId,
                name: data.Name || data.name || `Wohnung ${doc.id}`,
                address: data.address || "Zürichstrasse 50, 8340 Hinwil",
                price: data.Preis || 0,
                size: data.Grösse || 0,
                rooms: data.Zimmer || 0,
                floor: convertEtageToFloor(data.Etage),
                available: data.Verfügbar !== false,
                status: data.Status || (data.Verfügbar !== false ? 'available' : 'sold'), // Read status field from Firebase
                description: data.description || "",
                features: Array.isArray(data.features) ? data.features : [],
                images360: generateBuilding360Images(), // Use dummy 360° images
                thumbnail: getProfileImage(apartmentId), // Use new profile images
                floorPlan: `${cloudfrontUrl}/PDF-Grundrisse/${getFloorPlanFilename(apartmentId)}`, // Use new floor plan naming
                floorPlanImages: generateFloorPlanImages(apartmentId), // Generate floor plan WebP images
                constructionYear: data.constructionYear || "Frühjahr 2027",
                heatingType: data.heatingType || "Fernwärme",
                parking: data.parking || false,
                balcony: data.balcony || false,
                elevator: data.elevator || true,
                firestoreId: doc.id
              } as unknown as Apartment;
              
              updatedApartments.push(apartment);
            } catch (error) {
              console.warn(`Error processing apartment ${doc.id}:`, error);
            }
          });
          
          // Sort by numeric ID
          updatedApartments.sort((a, b) => a.id - b.id);
          setApartments(updatedApartments);
          setError(null);
          setConnectionStatus('connected');
          setLastUpdateTime(new Date());
          console.log('🔄 Live update received:', updatedApartments.length, 'apartments');
        },
        (err) => {
          console.error('Real-time listener error:', err);
          setError('Verbindung zu Live-Daten unterbrochen');
          setConnectionStatus('disconnected');
          
          // Automatic reconnection after 5 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('🔄 Attempting to reconnect...');
            setConnectionStatus('reconnecting');
            setupPersistentRealtimeListener();
          }, 5000);
        }
      );

      unsubscribeRef.current = unsubscribe;
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Failed to setup real-time listener:', error);
      setConnectionStatus('disconnected');
      setError('Fehler beim Aktivieren der Live-Updates');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Convert Firestore document ID (A1, B1) to numeric ID (1, 12)
  const getNumericId = (docId: string): number => {
    if (docId.startsWith('A')) {
      return parseInt(docId.substring(1));
    } else if (docId.startsWith('B')) {
      return parseInt(docId.substring(1)) + 11;
    }
    return 1;
  };

  // Convert Etage string to floor number
  const convertEtageToFloor = (etage: string): number => {
    if (!etage) return 0;
    if (etage === 'EG') return 0;
    if (etage === 'Attika') return 3;
    if (etage.includes('.')) {
      return parseInt(etage.split('.')[0]);
    }
    return 0;
  };

  const updateApartmentAvailability = async (id: number, available: boolean) => {
    // Optimistic UI update - update immediately for better UX
    const previousApartments = apartments;
    setApartments(prev => 
      prev.map(apt => 
        apt.id === id ? { ...apt, available } : apt
      )
    );

    try {
      if (useFirestore) {
        await ApartmentService.updateAvailability(id, available);
        console.log(`✅ Updated availability for apartment ${id}: ${available}`);
        // Real-time listener will confirm the change, but UI is already updated
      }
      setError(null);
    } catch (err) {
      console.error('Error updating availability:', err);
      // Rollback optimistic update on error
      setApartments(previousApartments);
      setError('Fehler beim Aktualisieren der Verfügbarkeit');
    }
  };

  const updateApartment = async (apartment: Apartment) => {
    // Store previous state for rollback
    const previousApartments = apartments;
    
    // Optimistic UI update
    setApartments(prev => 
      prev.map(apt => 
        apt.id === apartment.id ? apartment : apt
      )
    );

    try {
      if (useFirestore) {
        // Only update core fields that are editable
        const updates: Partial<Apartment> = {
          name: apartment.name,
          price: apartment.price,
          size: apartment.size,
          rooms: apartment.rooms,
          floor: apartment.floor,
          available: apartment.available,
          status: apartment.status // Include the new status field
        };
        
        await ApartmentService.updateApartment(apartment.id, updates);
        console.log(`✅ Updated apartment ${apartment.id}`);
        // Real-time listener will confirm the change, but UI is already updated
      }
      setError(null);
    } catch (err) {
      console.error('Error updating apartment:', err);
      // Rollback optimistic update on error
      setApartments(previousApartments);
      setError('Fehler beim Speichern der Wohnung');
      throw err;
    }
  };

  const resetApartments = () => {
    // Cleanup existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    setApartments(initialApartments);
    setUseFirestore(false);
    setConnectionStatus('disconnected');
    setError(null);
    setLastUpdateTime(null);
    console.log('🔄 Reset to static data');
  };

  return (
    <ApartmentContext.Provider value={{
      apartments,
      loading,
      error,
      updateApartmentAvailability,
      updateApartment,
      resetApartments,
      useFirestore,
      connectionStatus,
      lastUpdateTime
    }}>
      {children}
    </ApartmentContext.Provider>
  );
}; 